import { ViewportScroller } from '@angular/common';
import { inject, RESPONSE_INIT } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationError, ResolveFn, Router, UrlTree } from '@angular/router';
import { environment } from '@environments/environment';
import { SlugPage } from '@pages/slug-page/models';
import { SlugService } from '@pages/slug-page/services';
import { ConfiguratorLinkService } from '@shared/services/configurator-link';
import { DataLayerService } from '@shared/services/data-layer';
import { DidomiService } from '@shared/services/didomi';
import { CurrentLanguageService, LanguageService } from '@shared/services/language';
import { LogsService } from '@shared/services/logs';
import { PlatformContextService } from '@shared/services/platform-context';
import { ResponsiveService } from '@shared/services/responsive';
import { SeoService } from '@shared/services/seo';
import { Seo } from '@shared/services/seo/models';
import { TunnelReferrerService } from '@shared/services/tunnel-referrer';
import { getUuid } from '@shared/utils';
import { CommonStore, FunnelStore, PageStore } from '@stores';
import { filter, map, of, switchMap, take, tap } from 'rxjs';
import { catchError } from 'rxjs/operators';

/**
 * Open Graph or Twitter tags from the Drupal fields (`og_type`, `twitter_image`), named without their prefix: an
 * empty field falls back to the page title or description, or writes no tag.
 */
const socialTags = (
  prefix: 'og' | 'twitter',
  seo: Seo,
  fields: Record<string, string> = {}
): Record<string, string> => {
  const tags: Record<string, string | undefined> = { title: seo.title, description: seo.description };
  for (const [field, value] of Object.entries(fields)) {
    if (value) tags[field.replace(`${prefix}_`, '')] = value;
  }
  return Object.fromEntries(Object.entries(tags).filter((tag): tag is [string, string] => !!tag[1]));
};

/**
 * Resolver function that attempts to fetch a SlugPage.
 * Returns an observable of SlugPage, a UrlTree (for redirections), or undefined if an error occurs.
 * This resolver also handles an optional preview mode and replaces empty slugs with 'homepage'.
 */
export const slugPageResolver: ResolveFn<SlugPage | UrlTree | undefined> = (route, state) => {
  const baseUrl = inject(CommonStore).select('baseUrl');
  const seoService = inject(SeoService);
  const pageStore = inject(PageStore);
  const funnelStore = inject(FunnelStore);
  const configuratorLink = inject(ConfiguratorLinkService);
  const tunnelReferrer = inject(TunnelReferrerService);
  const platformContextService = inject(PlatformContextService);
  const isBrowser = platformContextService.isBrowser();
  const isServer = platformContextService.isServer();
  const logs = inject(LogsService);
  const languageService = inject(LanguageService);
  const currentLanguage = inject(CurrentLanguageService);
  const didomiService = inject(DidomiService);
  const dataLayerService = inject(DataLayerService);
  const router = inject(Router);
  const slugService = inject(SlugService);
  const breakpoints = inject(ResponsiveService).breakpoints;
  const viewportScroller = inject(ViewportScroller);
  const applicationType = environment.applicationType;
  if (!funnelStore.read()) {
    funnelStore.set({ validatedStep: 0, currentStep: 1 });
  }
  const urlPath: string = state.url.split(/[?#]/, 1)[0] ?? '';
  const rawSlugPath: string = urlPath.startsWith('/') ? urlPath.slice(1) : urlPath;
  const normalizedPath: string = rawSlugPath.trim().replace(/\/+$/, '');
  const isPreview: boolean = route.queryParamMap.get('preview') === 'true';
  const slug: string = normalizedPath === '' ? 'homepage' : normalizedPath;
  const canonicalLink: string = encodeURI(normalizedPath);

  const defaultApiUrl = new URL(environment.API.endpoint.default).origin;
  seoService.setPreconnectLink(defaultApiUrl);
  const defaultElasticSearchUrl = new URL(environment.API.endpoint.elasticsearch).origin;
  seoService.setPreconnectLink(defaultElasticSearchUrl);

  const responseInit = inject(RESPONSE_INIT, { optional: true });

  const CHECKOUT_STEPS_WITH_STORE_NAME = new Set<number>([2, 3, 4]);

  logs.trace('slugPageResolver', `Slug is "${slug}"`, isPreview ? 'on preview mode' : '');

  const updateSeoForSlugPage = (pageData: SlugPage): void => {
    const seo = pageData.config.seo;
    seoService.updateTitle(seo.title);
    seoService.updateMetaDescription(seo.description);
    seoService.setCanonicalLink(canonicalLink);
    seoService.setTwitterCard(socialTags('twitter', seo, seo.twitter));
    seoService.setOpenGraph({ ...socialTags('og', seo, seo.og), ...(seo.url ? { url: seo.url } : {}) });
    seoService.setAlternateLinks(pageData.type === 'page_404' ? {} : (seo.hrefLang ?? {}));
  };

  const emitGtmEvent = (pageData: SlugPage): void => {
    const country = currentLanguage.language.region;
    const gtmConfig = pageData.config.seo.gtmConfig;

    let checkout_store_front = '';
    const checkout_step = gtmConfig?.checkout_step ? Number(gtmConfig?.checkout_step) : undefined;
    if (checkout_step && CHECKOUT_STEPS_WITH_STORE_NAME.has(checkout_step)) {
      checkout_store_front = funnelStore.selectPath((state) => state.storeSelected?.name)() ?? '';
    }

    const event = {
      event: gtmConfig?.event || 'screen_view',
      env_country: country,
      env_work: applicationType,
      env_channel: breakpoints(),
      page_name: slug === 'homepage' ? '/' : `/${slug}`,
      page_type: gtmConfig?.page_type || undefined,
      page_cat1_name: gtmConfig?.page_cat1_name || undefined,
      page_cat2_name: gtmConfig?.page_cat2_name || undefined,
      page_cat3_name: gtmConfig?.page_cat3_name || undefined,
      checkout_step: checkout_step || undefined,
      checkout_type: gtmConfig?.checkout_type || undefined,
      checkout_store: gtmConfig?.checkout_store || checkout_store_front || undefined,
      tunnel_referrer: tunnelReferrer.read(pageData.type) || gtmConfig?.tunnel_referrer || undefined,
      group: gtmConfig?.group || undefined,
      description: gtmConfig?.description || undefined,
      context: gtmConfig?.context || undefined,
      event_type: 'view',
    } as const;
    // GTM reads the page URL and title when the event arrives, and the router writes them after resolvers.
    router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd || e instanceof NavigationCancel || e instanceof NavigationError),
        take(1),
        filter((e) => e instanceof NavigationEnd)
      )
      .subscribe(() => dataLayerService.push(event));
  };

  const { language } = currentLanguage;
  const readPage = (pageSlug: string) => slugService.readSlug(pageSlug, isPreview ? { preview: 'true' } : {});
  // In the application language, fixed at its creation: the domain default when the path is not served.
  const readNotFoundPage = () => readPage(language.urlPrefix ? `${language.urlPrefix}/404` : '404');

  const request = currentLanguage.resolve(state.url).served
    ? readPage(slug).pipe(
        switchMap((pageData) => {
          if (pageData.config.language === language.locale) return of(pageData);
          logs.error('slugPageResolver', 'The CMS page language differs from the URL language, answering a 404', {
            host: platformContextService.getHostname(),
            path: urlPath,
            urlLanguage: language.locale,
            cmsLanguage: pageData.config.language,
          });
          return readNotFoundPage();
        })
      )
    : readNotFoundPage();

  return request.pipe(
    take(1),
    map((pageData) => {
      pageData['blocks'] = pageData.blocks.map((block) => ({ ...block, _uuid: getUuid() }));
      return pageData;
    }),
    switchMap((pageData: SlugPage) =>
      languageService.use(language.locale).pipe(
        switchMap(() => didomiService.init()),
        tap(() => {
          logs.trace('slugPageResolver', 'Slug data:', pageData);
          emitGtmEvent(pageData);
          if (isBrowser) {
            viewportScroller.scrollToPosition([0, 0]);
          }
          updateSeoForSlugPage(pageData);
          pageData.url = slug;
          pageStore.set(pageData);
          configuratorLink.clearIfLeftFunnel(pageData.type);
          tunnelReferrer.clearIfLeftFunnel(pageData.type);
          if (pageData.type === 'page_404') {
            if (isServer) {
              if (responseInit) {
                responseInit.status = 404;
              }
            }
          }
        }),
        map((): SlugPage => pageData)
      )
    ),
    catchError((error: unknown) => {
      logs.fatal('slugPageResolver', 'Failed to resolve page', { error, normalizedPath });
      if (normalizedPath !== '') {
        void router.navigate([baseUrl() ?? '']);
      }
      return of(undefined);
    })
  );
};
